import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical } from "@/lib/seo";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { format, startOfDay, eachDayOfInterval, subDays } from "date-fns";
import { CalendarIcon, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { listRoomsAndBlocks, submitBookingRequest } from "@/lib/booking.functions";
import { computeEffectivePrice, type Season } from "@/lib/pricing";

export const Route = createFileRoute("/book")({
  head: () => ({
    meta: [
      { title: "Request to Book - Malak BnB Resort" },
      { name: "description", content: "Submit a reservation request at Malak BnB Resort. We personally confirm availability and send payment instructions." },
      { property: "og:title", content: "Request to Book - Malak BnB Resort" },
      { property: "og:description", content: "Submit a reservation request. We personally confirm your stay." },
      { property: "og:url", content: "https://malakbnbresort.ca/book" },
      { property: "og:image", content: "/images/hero-exterior.webp" },
    ],
    links: [canonical("/book")],
  }),
  component: BookPage,
});

function BookPage() {
  const listFn = useServerFn(listRoomsAndBlocks);
  const submitFn = useServerFn(submitBookingRequest);
  const { data } = useQuery({ queryKey: ["rooms-and-blocks"], queryFn: () => listFn() });

  const [submitted, setSubmitted] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkIn, setCheckIn] = useState<Date>();
  const [checkOut, setCheckOut] = useState<Date>();
  const [roomId, setRoomId] = useState<string>("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    guests: "2",
    notes: "",
    arrivalTime: "",
    arrivalUnsure: false,
  });

  const today = startOfDay(new Date());

  const blockedCheckInDates = useMemo(() => {
    if (!roomId || !data?.blocks) return [] as Date[];
    const dates: Date[] = [];
    for (const b of data.blocks) {
      if (b.room_id !== roomId) continue;
      const start = startOfDay(new Date(b.start_date));
      const lastNight = subDays(startOfDay(new Date(b.end_date)), 1);
      if (lastNight < start) continue;
      dates.push(...eachDayOfInterval({ start, end: lastNight }));
    }
    return dates;
  }, [roomId, data?.blocks]);

  const isBlockedCheckIn = (date: Date) =>
    blockedCheckInDates.some((d) => d.getTime() === startOfDay(date).getTime());

  const isBlockedCheckOut = (date: Date) => {
    if (!checkIn) return false;
    const start = startOfDay(checkIn);
    const end = subDays(startOfDay(date), 1);
    if (end < start) return false;
    const nights = eachDayOfInterval({ start, end });
    return nights.some((n) =>
      blockedCheckInDates.some((d) => d.getTime() === n.getTime()),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkIn || !checkOut || !roomId) return;
    setSubmitting(true);
    setError(null);
    setUnavailable(false);
    try {
      const res = await submitFn({
        data: {
          room_id: roomId,
          guest_name: form.name,
          email: form.email,
          phone: form.phone,
          check_in: format(checkIn, "yyyy-MM-dd"),
          check_out: format(checkOut, "yyyy-MM-dd"),
          guests: parseInt(form.guests, 10),
          notes: form.notes || undefined,
          arrival_time: form.arrivalUnsure ? "Not sure yet" : form.arrivalTime,
        },
      });
      if (!res.ok) {
        setUnavailable(true);
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="bg-card py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-heading text-foreground animate-fade-in-up">Request to Book</h1>
          <p className="mt-4 text-muted-foreground max-w-xl">
            Submit your reservation request below. This is not an instant booking - we'll personally
            confirm availability and share payment details with you directly.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 rounded-2xl bg-primary/5 border border-primary/20 p-5 flex gap-3">
            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <strong className="text-foreground">How it works:</strong> After you submit your request,
              we'll review availability and contact you to confirm. Payment is handled via Clover, debit,
              or e-transfer - never charged automatically.
            </div>
          </div>

          {submitted ? (
            <div className="rounded-2xl bg-primary/5 border border-primary/20 p-12 text-center">
              <h2 className="text-2xl font-heading text-foreground">Request Received!</h2>
              <p className="mt-4 text-muted-foreground max-w-md mx-auto">
                Thank you for your reservation request. We'll review availability and get back to you
                within 24 hours with confirmation and payment details.
              </p>
              <p className="mt-6 text-sm text-muted-foreground">
                Questions? Call us at{" "}
                <a href="tel:+12899298055" className="text-primary hover:underline">1-289-929-8055</a>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              {/* Room */}
              <div>
                <label htmlFor="room" className="block text-sm font-medium text-foreground mb-1.5">Room *</label>
                <select
                  id="room"
                  required
                  value={roomId}
                  onChange={(e) => setRoomId(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select a room…</option>
                  {data?.rooms.map((r) => {
                    const price = computeEffectivePrice(
                      Number(r.price_cad),
                      checkIn,
                      (data?.seasons ?? []) as Season[],
                    );
                    return (
                      <option key={r.id} value={r.id}>
                        {r.name} - C${price}/night
                      </option>
                    );
                  })}
                </select>
                {roomId && (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Greyed-out dates on the calendar are unavailable for this room.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1.5">Full Name *</label>
                <input
                  id="name" type="text" required maxLength={100}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">Email *</label>
                <input
                  id="email" type="email" required maxLength={255}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-1.5">Phone Number *</label>
                <input
                  id="phone" type="tel" required maxLength={20}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="1-XXX-XXX-XXXX"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Check-In Date *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          "w-full rounded-xl border border-input bg-background px-4 py-3 text-sm text-left flex items-center gap-2",
                          !checkIn && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="h-4 w-4" />
                        {checkIn ? format(checkIn, "PPP") : "Select date"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkIn}
                        onSelect={(d) => {
                          setCheckIn(d);
                          if (d && checkOut && checkOut <= d) setCheckOut(undefined);
                        }}
                        disabled={(date) => date < today || isBlockedCheckIn(date)}
                        modifiers={{ blocked: blockedCheckInDates }}
                        modifiersClassNames={{ blocked: "line-through opacity-50" }}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Check-Out Date *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          "w-full rounded-xl border border-input bg-background px-4 py-3 text-sm text-left flex items-center gap-2",
                          !checkOut && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="h-4 w-4" />
                        {checkOut ? format(checkOut, "PPP") : "Select date"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkOut}
                        onSelect={setCheckOut}
                        disabled={(date) => {
                          const min = checkIn ? subDays(startOfDay(checkIn), -1) : today;
                          if (date < min) return true;
                          return isBlockedCheckOut(date);
                        }}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div>
                <label htmlFor="guests" className="block text-sm font-medium text-foreground mb-1.5">Number of Guests *</label>
                <select
                  id="guests"
                  required
                  value={form.guests}
                  onChange={(e) => setForm({ ...form, guests: e.target.value })}
                  className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <option key={n} value={String(n)}>{n} {n === 1 ? "Guest" : "Guests"}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Estimated Arrival Time *</label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    id="arrivalTime" type="text" required={!form.arrivalUnsure} disabled={form.arrivalUnsure} maxLength={50}
                    value={form.arrivalUnsure ? "" : form.arrivalTime}
                    onChange={(e) => setForm({ ...form, arrivalTime: e.target.value })}
                    className="flex-1 rounded-xl border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                    placeholder="e.g. 3:00 PM or around 5pm"
                  />
                  <label className={cn(
                    "flex items-center gap-2 rounded-xl border px-4 py-3 text-sm cursor-pointer transition-colors shrink-0",
                    form.arrivalUnsure ? "border-primary bg-primary/5" : "border-input bg-background",
                  )}>
                    <input
                      type="checkbox"
                      checked={form.arrivalUnsure}
                      onChange={(e) => setForm({ ...form, arrivalUnsure: e.target.checked, arrivalTime: "" })}
                      className="accent-primary h-4 w-4"
                    />
                    <span>Not sure yet</span>
                  </label>
                </div>
              </div>



              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-foreground mb-1.5">Special Requests</label>
                <textarea
                  id="notes"
                  maxLength={1000}
                  rows={4}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  placeholder="Any special requests or questions..."
                />
              </div>

              {unavailable && (
                <p className="text-sm text-destructive">
                  Sorry, those dates are unavailable for that room. Please pick different dates or another room.
                </p>
              )}
              {error && <p className="text-sm text-destructive">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg mt-2 disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Submit Reservation Request"}
              </button>
              <p className="text-xs text-muted-foreground text-center">
                This is a request only. No payment is charged until we confirm your stay. Requests are subject to our{" "}
                <Link to="/terms-and-cancellation-policy" className="text-primary font-semibold hover:underline">
                  cancellation policy
                </Link>
                .
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
